
import { defineComponent } from 'vue';
import Pagination from '@/components/Pagination/index.vue';
import { mapActions, mapGetters } from 'vuex';
import axios from 'axios';
import { EventBus } from '@/eventBus';
import * as CONSTANTS from '@/constants';
import LoadController from '../../js/LoadController.js';
import gql from 'graphql-tag';
import { attachmentDownload } from '@/api/attachment';
import { format, startOfToday } from 'date-fns';

// DIALOGS
import DisputeDialog from '@/components/_dialogs/DisputeDialog.vue';
import InvoiceDisputesDialog from '@/components/_dialogs/InvoiceDisputesDialog.vue';
import InterchangeLinks from '@/components/_Invoice/InterchangeLinks.vue';
import PlanManualActionDialog from '@/components/_dialogs/PlanManualActionDialog.vue';
import { actionAddInvoiceCopyEmail, actionAddInvoiceCopyMail, actionAddInvoiceGroupCopyEmail } from '@/api/action';
import AttachmentHelper from '@/helpers/attachment';
import Logging from '@/logging';
import CustomFieldLabel from '@/components/_CustomFields/CustomFieldLabel.vue';
import CustomFieldValue from '@/components/_CustomFields/CustomFieldValue.vue';
import { Risolto_Channel_Enum, Risolto_Communication_Template, Risolto_Invoice_Document, Risolto_Tone_Of_Voice_Enum } from '@/generated/graphql';
import AttachmentTemplatesDialog from '@/components/_dialogs/AttachmentTemplatesDialog.vue';
import CustomFieldsFilters from '@/components/_CustomFields/CustomFieldsFilters.vue';
import Utils from '@/utils/utils';

const ATTACHMENT_TEMPLATES = gql`
query comonentsInvoiceInvoiceTable__attachmentTemplates($participant: uuid!) {
  attachmentTemplates: risolto_communication_template(
    where:{
      participant: { _eq: $participant }
      ui_template_configuration: { _is_null: false }
    }
  ) {
    reference
  }
}`;

const GET_LEGAL_ENTITIES_COUNT = gql`
	query getLegalEntitiesCount($participant: uuid!) {
		participant: risolto_participant(where: { reference: { _eq: $participant } }) {
			legal_entities_aggregate {
				aggregate {
					count
				}
			}
		}
	}
`;

const GET_ACTION = gql`
	query getAction($action: uuid!, $participant: uuid!) {
		action: risolto_action(where: { reference: { _eq: $action }, participant: { _eq: $participant } }) {
			status
			channel
			recipient
			recipient_by_reference {
				address
				balance_amount
				dunning_metadata
				last_dunning_at
				legal_id
				legal_id_type
				name
				account_id
				type
				updated_by
				participant
				reference
				legal_entity
				legal_entity_by_reference {
					reference
					registration_name
				}
			}
			reference
			metadata
			message_code
			participant
			primary_invoice
			primary_invoice_by_reference {
				invoice_number
			}
			action_invoices {
				reference
				relation
				invoice_by_reference {
					invoice_number
					open_amount
					total_amount
					payment_reference
					due_date
					issue_date
					status
					recipient
					recipient_by_reference {
						reference
						name
					}
					legal_entity
					legal_entity_by_reference {
						reference
						registration_name
					}
					participant
					participant_by_reference {
						name
						reference
						logical_id
					}
					reference
					amount_currency
					interchange_links {
						interchange_by_reference {
							reference
							channel
							direction
						}
					}
				}
			}
			legal_entity
			execution_type
			source
			executed_at
			original_action
			retry_count
			start_time
			due_date
			recipient
			recipient_by_reference {
				reference
				name
				account_id
				participant
			}
			legal_entity_by_reference {
				registration_name
				outbound_channel: processing_details(path: "outbound_channel")
			}
			created_at
			assignee
			assignee_by_reference {
				username
			}
			channel_endpoint
			channel_endpoint_by_reference {
				channel
				endpoint
				endpoint_details
				deactivated_at
				deactivated_by
				last_failed_delivery_at
				last_failed_delivery_type
				locale
				priority
			}
		}
	}
`;

const GET_USER_INTERFACE_DETAILS = gql`
query ComponentsInvoiceTable_userInterfaceDetails($participant: uuid!) {
	userInterfaceDetails: risolto_participant(
		where: {
			reference: { _eq: $participant }
		}
	) {
		custom_actions: user_interface_details(path: "invoice.custom_actions")
		custom_recipient_actions: user_interface_details(path: "recipient.custom_actions")
		custom_field_definitions: user_interface_details(path: "invoice.custom_field_definitions")
	}
}`;
const GET_PARTICIPANT = gql`
	query componentsInvoiceInvoiceTable_participant($participant: uuid!) {
		participant: risolto_participant(where: { reference: { _eq: $participant } }) {
			logical_id
		}
	}
`;

export default defineComponent({
	apollo: {
		participant: {
			query: GET_PARTICIPANT,
			variables() {
				return {
					participant: this.selectedParticipantStore
				};
			},
			update(data) {
				return data.participant[0];
			}
		},
		userInterfaceDetails: {
			query: GET_USER_INTERFACE_DETAILS,
			variables() {
				return {
					participant: this.selectedParticipantStore
				};
			},
			update: data => data.userInterfaceDetails[0],
			skip() {
				return !this.formattedData; // Invoices
			}
		},
		attachmentTemplates: {
			query: ATTACHMENT_TEMPLATES,
			variables() {
				return {
					participant: this.selectedParticipantStore
				};
			},
			skip() {
				return !this.selectedParticipantStore;
			}
		}
	},
	name: 'InvoiceTable',
	components: {
		Pagination,
		DisputeDialog,
		InvoiceDisputesDialog,
		InterchangeLinks,
		PlanManualActionDialog,
		CustomFieldLabel,
		CustomFieldValue,
		CustomFieldsFilters,
		AttachmentTemplatesDialog
	},
	props: {
		showActions: {
			type: Boolean,
			default: () => false
		},
		disablePaymentPlan: {
			type: Boolean,
			default: () => false
		},
		disablePaymentPlanReason: {
			type: String,
			default: () => null
		},
		showFilters: {
			type: Boolean,
			default: () => false
		},
		highlightCurrentRowProp: {
			type: Boolean,
			default: () => true
		},
		fullRecipient: {
			type: Object,
			default: () => {}
		},
		recipient: {
			type: String,
			default: () => null
		},
		propsDocumentType: {
			type: Array,
			default: () => []
		},
		propsColumns: {
			type: Object,
			default: () => {
				return {
					manualActionPossible: false,
					selection: false,
					deliveryDetails: true,
					invoiceNumber: true,
					legalEntity: true,
					lastDunningStatus: false,
					recipient: true,
					paymentReference: true,
					status: true,
					issueDate: true,
					dueDate: true,
					paidAtDate: true,
					totalAmount: true,
					openAmount: true,
					showGoTo: false,
					pdf: true,
					processingDueDate: true
				};
			}
		},
		propsStatuses: {
			type: Array,
			default: () => []
		},
		propsPage: {
			type: Number,
			default: null
		},
		propsLimit: {
			type: Number,
			default: null
		},
		propsSort: {
			type: Object,
			default: () => null
		}
	},
	data() {
		return {
			legalEntityCount: 0,
			loadController: new LoadController(),
			invoiceFiles: {},
			actionDetailDialogVisible: false,
			deliveryEvents: {},
			total: null,
			query_id: null,
			sendInvoiceCopyMailloading: false,
			sendInvoiceCopyEmailloading: false,
			sendAccountOverviewActionEmailLoading: false,
			sendAccountOverviewActionSmsLoading: false,
			manualAttachmentLoading: false,
			disputeDialogVisible: false,
			invoiceDisputesDialogVisible: false,
			selectedInvoice: undefined,
			documentTypes: ['INVOICE', 'CREDIT_NOTE', 'DEBIT_NOTE', 'PAYMENT'],
			pickerOptions: {
				shortcuts: [
					{
						text: this.$t('lastWeek'),
						onClick(picker) {
							const end = new Date();
							const start = new Date();
							start.setTime(start.getTime() - 3600 * 1000 * 24 * 7);
							picker.$emit('pick', [start, end]);
						}
					},
					{
						text: this.$t('lastMonth'),
						onClick(picker) {
							const end = new Date();
							const start = new Date();
							start.setTime(start.getTime() - 3600 * 1000 * 24 * 30.5);
							picker.$emit('pick', [start, end]);
						}
					},
					{
						text: this.$t('last3months'),
						onClick(picker) {
							const end = new Date();
							const start = new Date();
							start.setTime(start.getTime() - 3600 * 1000 * 24 * 91);
							picker.$emit('pick', [start, end]);
						}
					},
					{
						text: this.$t('last6months'),
						onClick(picker) {
							const end = new Date();
							const start = new Date();
							start.setTime(start.getTime() - 3600 * 1000 * 24 * 183);
							picker.$emit('pick', [start, end]);
						}
					},
					{
						text: this.$t('lastYear'),
						onClick(picker) {
							const end = new Date();
							const start = new Date();
							start.setTime(start.getTime() - 3600 * 1000 * 24 * 365);
							picker.$emit('pick', [start, end]);
						}
					}
				]
			},
			userInterfaceDetails: {
				custom_actions: [],
				custom_field_definitions: []
			},
			customFieldsFiltersVisible: [],
			customFieldsFiltersObject: {},
			customFieldsFiltersKey: 0,
			singleSelectedInvoiceHasReference: false,
			planManualActionDialogVisible: false
		};
	},
	computed: {
		CONSTANTS() {
			return CONSTANTS;
		},
		...mapGetters(['env', 'beta', 'selectedParticipantStore', 'selectedLegalEntityStore', 'selectedInvoices']),

		dateRange: {
			get() {
				return this.$store.getters.invoicesDateRange;
			},
			set(value) {
				this.$store.commit('invoices/SET_DATE_RANGE', value);
			}
		},
		status: {
			get() {
				if (this.propsStatuses && this.propsStatuses.length > 0) {
					return this.propsStatuses;
				}
				return this.$store.getters.invoicesStatus;
			},
			set(value) {
				this.$store.commit('invoices/SET_STATUS', value);
			}
		},
		documentType: {
			get() {
				if (this.propsDocumentType && this.propsDocumentType.length > 0) {
					return this.propsDocumentType;
				}
				return this.$store.getters.invoicesDocumentType;
			},
			set(value) {
				this.$store.commit('invoices/SET_DOCUMENT_TYPE', value);
			}
		},
		searchTerm: {
			get() {
				return this.$store.getters.invoicesSearchTerm;
			},
			set(value) {
				this.$store.commit('invoices/SET_SEARCHTERM', value);
			}
		},
		offset: {
			get() {
				if (this.propsPage !== null && this.propsLimit !== null) {
					return (this.propsPage - 1) * this.propsLimit;
				}
				return this.$store.getters.invoicesOffset;
			},
			set(value) {
				if (this.propsPage !== null) return;
				this.$store.commit('invoices/SET_OFFSET', value);
			}
		},
		page: {
			get() {
				if (this.propsPage !== null) {
					return this.propsPage;
				}
				return this.$store.getters.invoicesPage;
			},
			set(value) {
				if (this.propsPage !== null) {
					this.$emit('update:propsPage', value);
					return;
				}
				this.$store.commit('invoices/SET_PAGE', value);
			}
		},
		limit: {
			get() {
				if (this.propsLimit !== null) {
					return this.propsLimit;
				}
				return this.$store.getters.invoicesLimit;
			},
			set(value) {
				if (this.propsLimit !== null) {
					this.$emit('update:propsLimit', value);
					return;
				}
				this.$store.commit('invoices/SET_LIMIT', value);
			}
		},
		sort: {
			get() {
				if (this.propsSort !== null) {
					return this.propsSort;
				}
				return this.$store.getters.invoicesSort;
			},
			set(value) {
				if (this.propsSort !== null) {
					this.$emit('update:propsSort', value);
					return;
				}
				this.$store.commit('invoices/SET_SORT', value);
			}
		},

		loading() {
			return !this.loadController.failed && this.loadController.data == null;
		},
		formattedData() {
			return this.loadController.data == null ? [] : this.loadController.data;
		},
		selectedInvoiceReferences() {
			return this.selectedInvoices.map((invoice) => {
				return invoice.reference;
			});
		},
		showManualAttachmentButton() {
			return this.attachmentTemplates && this.attachmentTemplates.length > 0;
		},
		sortable() {
			if (this.recipient && this.recipient !== '') {
				// Allow sorting within a recipient (scoped dataset)
				return 'custom';
			}
			if (this.searchTerm.trim() === '') {
				// Don't allow sorting on the full table without search
				// Performance consideration: full dataset sorting is expensive
				return false;
			}
			// Allow sorting when there's a search term (filtered dataset)
			return 'custom';
		}
	},
	watch: {
		recipient() {
			this.fetchInvoices();
		}
	},
	created() {
		if (this.selectedParticipantStore) {
			if (this.status.length === 0) {
				this.status = CONSTANTS.INVOICETABLE_DEFAULT_STATUS_LIST;
			}

			this.fetchInvoices();
		}
	},
	mounted() {
		EventBus.$on('clearSelectedInvoices', () => {
			Logging.log('InvoiceTable.clearSelectedInvoices');
			this.multipleSelection = [];
			this.$store.dispatch('risolto/setSelectedInvoices', []);
			this.$refs.multipleTable.clearSelection();
		});

		this.fetchLegalEntityCount();
	},
	destroyed() {
		EventBus.$off('clearSelectedInvoices');

		this.invoiceFiles = {};
		this.deliveryEvents = {};
	},
	methods: {
		...mapActions('action', ['sendInvoiceCopyMail']),
		// Wrapper method to use Utils.computedStatus in template
		computedStatus(openAmount: number, dueDate: string | null): string {
			return Utils.computedStatus(openAmount, dueDate);
		},
		isPdf(attachment) {
			return AttachmentHelper.isPdf(attachment);
		},
		isXml(attachment) {
			return AttachmentHelper.isXml(attachment);
		},
		handleInvoiceDisputeConfirm() {
			this.fetchInvoices();
			EventBus.$emit('fetchActions_recipient', {});
		},
		handleInvoiceDisputeChange() {
			this.fetchInvoices();
			EventBus.$emit('fetchActions_recipient', {});
		},
		handleCreatePhoneAction() {
			this.$store.commit('actions/SET_CREATE_PHONE_ACTION_OVERLAY_VISIBLE', true);
			this.$store.commit('actions/SET_CREATE_PHONE_ACTION_OVERLAY_INVOICES', this.selectedInvoices);
			this.$store.commit('actions/SET_CREATE_PHONE_ACTION_OVERLAY_RECIPIENT', this.fullRecipient);
		},
		handlePlanManualAction() {
			this.planManualActionDialogVisible = true;
			EventBus.$emit('fetchActions_recipient', {});
		},
		async handleSendAccountOverviewEmail() {
			this.sendAccountOverviewActionEmailLoading = true;
			try {
				await this.handleSendAccountOverview(Risolto_Channel_Enum.Email);
			} finally {
				this.sendAccountOverviewActionEmailLoading = false;
			}
		},
		async handleSendAccountOverviewSms() {
			this.sendAccountOverviewActionSmsLoading = true;
			try {
				await this.handleSendAccountOverview(Risolto_Channel_Enum.Sms);
			} finally {
				this.sendAccountOverviewActionSmsLoading = false;
			}
		},
		async handleSendAccountOverview(channel: Risolto_Channel_Enum) {
			this.sendAccountOverviewActionLoading = true;

			const payload = {
				participant: this.$store.state.risolto.selectedParticipantStore,
				legal_entity: this.fullRecipient.legal_entity,
				recipient: this.fullRecipient.reference,
				channel: channel
			};

			const response = await axios.post(`https://${process.env.VUE_APP_API_ENDPOINT}/action/create-account-overview`, payload, {
				headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
			});
			const result = await this.$apollo.query({
				query: GET_ACTION,
				variables: {
					participant: this.$store.state.risolto.selectedParticipantStore,
					action: response.data.action.reference
				}
			});
			const action = result.data.action[0];

			this.$store.commit('actions/UPSERT_ACTION', action);
			this.$store.commit('actions/SET_SELECTED_ACTION_REF', action.reference);
			this.$store.commit('risolto/SET_SELECTED_RECIPIENT', action.recipient_by_reference);
			this.$store.commit('risolto/SET_COMPONENT_TYPE_STORE', action.channel);
			this.$store.commit('risolto/SET_ACTION_DETAIL_DIALOG_VISIBLE', true);
		},
		async handleManualAttachmentButtonClick() {
			this.manualAttachmentLoading = true;
			const template: Risolto_Communication_Template = await this.$refs.attachmentTemplatesDialog.show();

			if (template == null) {
				this.manualAttachmentLoading = false;
				return;
			}

			const payload = {
				channel: template.channel,
				tone_of_voice: Risolto_Tone_Of_Voice_Enum.Friendly,
				locale: template.locale,
				template_id: template.template_id,
				participant: this.$store.state.risolto.selectedParticipantStore,
				template_query_variables: {
					participant: this.$store.state.risolto.selectedParticipantStore,
					recipient: this.fullRecipient.reference,
					invoices: this.selectedInvoices.map((invoice: Risolto_Invoice_Document) => invoice.reference)
				}
			};

			axios
				.post(`https://${process.env.VUE_APP_API_ENDPOINT}/templating/frontend-render/v1`, payload, {
					headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
				})
				.then(result => {
					if (result.data.message_code === 'generic_failure') {
						this.$notify({
							title: this.$t('somethingWentWrong'),
							type: 'error'
						});
						this.manualAttachmentLoading = false;
						return;
					}
					AttachmentHelper.showDocument(result.data.base64_source || result.data.base64, result.data.content_type || 'text/html');
				})
				.catch(error => {
					Logging.error(error);
					this.$notify({
						title: this.$t('somethingWentWrong'),
						type: 'error'
					});
				})
				.finally(() => {
					this.manualAttachmentLoading = false;
				});
		},
		async handleDisputeInvoices() {
			this.disputeDialogVisible = true;
		},
		handleCreatePaymentPlanButtonClick() {
			this.$router.push({
				name: 'create-payment-plan',
				params: { participant: this.selectedParticipantStore },
				query: { recipient: this.recipient }
			});
		},
		async handleSendInvoiceCopyMail() {
			if (this.sendInvoiceCopyMailloading) {
				return;
			}
			this.sendInvoiceCopyMailloading = true;

			const recipient = this.recipient;
			const invoices = this.selectedInvoices;

			actionAddInvoiceCopyMail(
				recipient,
				invoices.map((invoice) => {
					return invoice.reference;
				})
			)
				.then((result) => {
					var action = result.data.action;

					this.$store.commit('actions/UPSERT_ACTION', action, { root: true });
					this.$store.commit('actions/SET_SELECTED_ACTION_REF', action.reference, { root: true });
					this.$store.commit('risolto/SET_SELECTED_RECIPIENT', action.recipient_by_reference, { root: true });
					this.$store.commit('risolto/SET_COMPONENT_TYPE_STORE', action.channel, { root: true });
					this.$store.commit('risolto/SET_ACTION_DETAIL_DIALOG_VISIBLE', true, { root: true });

					this.sendInvoiceCopyMailloading = false;
				})
				.catch((e) => {
					this.sendInvoiceCopyMailloading = false;
				});
		},
		async handleSendInvoiceCopyEmail() {
			if (this.sendInvoiceCopyEmailloading) {
				return;
			}
			this.sendInvoiceCopyEmailloading = true;

			if (this.selectedInvoices.length === 1) {
				actionAddInvoiceCopyEmail(this.selectedParticipantStore, this.fullRecipient.legal_entity, this.fullRecipient.reference, this.selectedInvoices[0].reference)
					.then((result) => {
						var action = result.data.action;

						this.$store.commit('actions/UPSERT_ACTION', action);
						this.$store.commit('actions/SET_SELECTED_ACTION_REF', action.reference);
						this.$store.commit('risolto/SET_SELECTED_RECIPIENT', action.recipient_by_reference);
						this.$store.commit('risolto/SET_COMPONENT_TYPE_STORE', action.channel);
						this.$store.commit('risolto/SET_ACTION_DETAIL_DIALOG_VISIBLE', true);
					})
					.finally(() => {
						this.sendInvoiceCopyEmailloading = false;
					});
			} else {
				actionAddInvoiceGroupCopyEmail(
					this.selectedParticipantStore,
					this.fullRecipient.legal_entity,
					this.fullRecipient.reference,
					this.selectedInvoices.map((invoice) => {
						return invoice.reference;
					})
				)
					.then((result) => {
						var action = result.data.action;

						this.$store.commit('actions/UPSERT_ACTION', action);
						this.$store.commit('actions/SET_SELECTED_ACTION_REF', action.reference);
						this.$store.commit('risolto/SET_SELECTED_RECIPIENT', action.recipient_by_reference);
						this.$store.commit('risolto/SET_COMPONENT_TYPE_STORE', action.channel);
						this.$store.commit('risolto/SET_ACTION_DETAIL_DIALOG_VISIBLE', true);
					})
					.finally(() => {
						this.sendInvoiceCopyEmailloading = false;
					});
			}
		},
		handleCustomActionClick(row, action) {
			Logging.log('handleCustomActionClick', row, action);
			const payload = {
				participant: row.participant,
				legal_entity: row.legal_entity,
				recipient: row.recipient_by_reference.reference,
				document_type: row.document_type,
				document_number: row.document_number,
				user: this.$store.state.risolto.loggedInUser.reference
			};

			this.$prompt(this.$t('custom_action.confirm', { invoice_number: row.document_number }), action.label[this.$i18n.locale], {
				confirmButtonText: this.$t('Ok'),
				cancelButtonText: this.$t('cancel'),
				inputPattern: new RegExp(`^${row.document_number}$`),
				inputErrorMessage: this.$t('validator.invalid_number')
			}).then(async ({ value }) => {
				this.$message({
					type: 'info',
					message: this.$t('custom_action.started')
				});
				const response = await axios.post(action.endpoint, payload, {
					headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
				});
				if (response.data && response.data.redirect) {
					Utils.openInNewTab(response.data.redirect);
				}
				this.$forceUpdate();
				this.$message({
					type: 'info',
					message: this.$t('custom_action.executed')
				});
			}).catch(() => {
				this.$message({
					type: 'info',
					message: this.$t('custom_action.not_executed')
				});
			});
		},
		async handleCustomRecipientActionClick(recipient, action) {
			Logging.log('handleCustomRecipientActionClick', recipient, action);
			const payload = {
				participant: recipient.participant,
				legal_entity: recipient.legal_entity,
				recipient: recipient.reference
			};

			let response = await axios.post(action.endpoint, payload, {
				headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
			});

			if (response.data.redirect) {
				Utils.openInNewTab(response.data.redirect);
			}
		},
		handleCurrentChange(row) {
			this.$emit('highlightedChange', row);
		},
		onSortChange({ prop, order }) {
			Logging.log('InvoiceTable.onSortChangle');
			Logging.log('sortChange: ', prop, order);

			let gqlOrder: string | null = '';
			let gqlOrderObject = {};

			if (order === 'ascending') {
				gqlOrder = 'asc_nulls_last';
			} else if (order === 'descending') {
				gqlOrder = 'desc_nulls_last';
			} else if (order === null) {
				gqlOrder = null;
			}

			if (prop) {
				if (prop === 'legal_entity_by_reference.registration_name') {
					gqlOrderObject = { legal_entity_by_reference: { registration_name: gqlOrder } };
				} else if (prop === 'recipient_by_reference.name') {
					gqlOrderObject = { recipient_by_reference: { name: gqlOrder } };
				} else if (prop === 'status') {
					gqlOrderObject = { open_amount: gqlOrder, issue_date: gqlOrder };
				} else {
					gqlOrderObject[prop] = gqlOrder;
				}

				Logging.log('gqlorderobject: ', gqlOrderObject);
			}

			this.sort = gqlOrderObject;
			this.$nextTick(() => {
				this.fetchInvoices();
			});
		},
		async onClickDocIcon(val) {
			attachmentDownload(this.selectedParticipantStore, val);
		},
		applyFilters() {
			// Reset pagination when applying filters
			this.page = 1;
			this.offset = 0;
			// Always fetch invoices regardless of status length
			this.fetchInvoices();
		},
		actualQueryVars(isCount) {
			var actualQueryVars: Record<string, any> = {
				participantRef: this.selectedParticipantStore
			};

			if (this.documentType && this.documentType.length > 0) {
				actualQueryVars.documentType = this.documentType;
			}

			if (this.showFilters) {
				if (this.dateRange) {
					actualQueryVars.startTimeFrom = this.dateRange[0];
					actualQueryVars.startTimeUntil = this.dateRange[1];
				}

				if (this.searchTerm && this.searchTerm.trim() !== '') {
					actualQueryVars.searchTerm = `%${this.searchTerm}%`;
				}

				if (this.customFieldsFiltersObject && this.customFieldsFiltersObject.custom_fields) {
					actualQueryVars.customFieldsFilter = this.customFieldsFiltersObject;
				}
			}

			if (this.recipient) {
				actualQueryVars.recipient = this.recipient;
			}
			if (!isCount) {
				actualQueryVars.orderBy = this.sort;
				actualQueryVars.limit = this.limit;
				actualQueryVars.offset = this.offset;
			}

			Logging.log('actualQueryVars', actualQueryVars);
			return actualQueryVars;
		},
		fetchLegalEntityCount() {
			this.$apollo
				.query({
					query: GET_LEGAL_ENTITIES_COUNT,
					variables: { participant: this.selectedParticipantStore }
				})
				.then(({ data }) => {
					var legalEntityCount = data.participant[0].legal_entities_aggregate.aggregate.count;
					this.legalEntityCount = legalEntityCount;
				});
		},
		async fetchInvoices() {
			this.deliveryEvents = {};
			Logging.log('InvoiceTable.fetchInvoices');
			this.loadController.clear();
			this.loadController.didAsk = true;
			this.loadController.failed = false;

			// Data & Total are separate queries: keep them in sync using the (current) query id
			const current_query_id = (Math.random() + 1).toString(36).substring(7);
			try {
				this.query_id = current_query_id;

				// Fetch the total counter separately
				this.$apollo
					.query({
						query: this.makeQuery(false, this.actualQueryVars(true)),
						variables: this.actualQueryVars(true)
					})
					.then(({ data }) => {
						if (current_query_id === this.query_id) {
							this.total = data.total.aggregate.count;
						}
					});

				let response = await this.$apollo
					.query({
						query: this.makeQuery(true, this.actualQueryVars(false)),
						variables: this.actualQueryVars(false)
					});
				Logging.log('InvoiceTable.fetchInvoices.apollo - data: ', response);
				this.loadController.data = response.data.invoices;
				this.loadController.didAsk = false;

				this.fetchAllDocumentData();
			} catch (e) {
				Logging.log('ERROR InvoiceTable.fetchInvoices', e);
				this.loadController.data = null;
				this.loadController.failed = true;
			}
		},

		fetchAllDocumentData() {
			if (!this.loadController.data || this.loadController.data.length === 0) {
				return;
			}

			this.loadController.data.forEach((doc, index) => {
				this.fetchDocumentDataFor(doc, index);
			});
		},

		async fetchDocumentDataFor(doc, index) {
			if (!this.loadController.data) {
				return;
			}

			try {
				const query = this.createDocumentQuery(doc.document_type);
				const variables = this.createDocumentVariables(doc);

				const response = await this.$apollo.query({ query, variables });

				const additionalData = this.extractDocumentData(response.data, doc.document_type);

				if (additionalData) {
					const updatedDoc = this.mergeDocumentData(doc, additionalData);
					this.$set(this.loadController.data, index, updatedDoc);
				}
			} catch (error) {
				Logging.log(`Error fetching data for ${doc.document_type} ${doc.document_number}:`, error);
			}
		},

		createDocumentQuery(documentType) {
			switch (documentType) {
				case 'INVOICE':
					return gql`
						query getInvoiceData(
							$participant: uuid!
							$documentNumber: String!
							$legalEntity: uuid
						) {
							document: risolto_invoice_document(
								where: {
									_and: [
										{ participant: { _eq: $participant } }
										{ invoice_number: { _eq: $documentNumber } }
										{ _or: [
											{ legal_entity: { _eq: $legalEntity } }
											{ legal_entity: { _is_null: true } }
										] }
									]
								}
								limit: 1
							) {
								invoice_number
								reference
								dunning_status
								derived_paid_at
								legal_entity
								invoice_dispute: disputes(
									where: { resolved_at: { _is_null: true } }
									order_by: { until: desc }
									limit: 1
								) {
									reference
									until
									dispute_code_by_reference {
										code
									}
								}
								actions(where: { executed_at: { _is_null: false } }) {
									reference
									action_interchanges {
										reference
										interchange_by_reference {
											reference
											interchange_events {
												event_type
												event_type_count
												last_event_timestamp
												email: channel_endpoint(path: "$.email")
												postal_mail: channel_endpoint(path: "postal_mail")
											}
											channel
											direction
										}
									}
								}
								file_references
							}
						}
					`;
				case 'CREDIT_NOTE':
					return gql`
						query getCreditNoteData(
							$participant: uuid!
							$documentNumber: String!
							$legalEntity: uuid
						) {
							document: risolto_credit_note(
								where: {
									_and: [
										{ participant: { _eq: $participant } }
										{ credit_note_number: { _eq: $documentNumber } }
										{ _or: [
											{ legal_entity: { _eq: $legalEntity } }
											{ legal_entity: { _is_null: true } }
										] }
									]
								}
								limit: 1
							) {
								credit_note_number
								reference
								dunning_status
								file_references
								legal_entity
							}
						}
					`;
				case 'DEBIT_NOTE':
					return gql`
						query getDebitNoteData(
							$participant: uuid!
							$documentNumber: String!
							$legalEntity: uuid
						) {
							document: risolto_debit_note(
								where: {
									_and: [
										{ participant: { _eq: $participant } }
										{ debit_note_number: { _eq: $documentNumber } }
										{ _or: [
											{ legal_entity: { _eq: $legalEntity } }
											{ legal_entity: { _is_null: true } }
										] }
									]
								}
								limit: 1
							) {
								debit_note_number
								reference
								dunning_status
								file_references
								legal_entity
							}
						}
					`;
				case 'PAYMENT':
					return gql`
						query getPaymentData(
							$participant: uuid!
							$documentNumber: String!
							$legalEntity: uuid
						) {
							document: risolto_payment_document(
								where: {
									_and: [
										{ participant: { _eq: $participant } }
										{ payment_number: { _eq: $documentNumber } }
										{ _or: [
											{ legal_entity: { _eq: $legalEntity } }
											{ legal_entity: { _is_null: true } }
										] }
									]
								}
								limit: 1
							) {
								payment_number
								reference
								dunning_status
								file_references
								legal_entity
							}
						}
					`;
				default:
					return null;
			}
		},

		// Create variables for single document query
		createDocumentVariables(doc) {
			return {
				participant: this.selectedParticipantStore,
				documentNumber: doc.document_number,
				legalEntity: doc.legal_entity || null
			};
		},

		// Extract data from query response
		extractDocumentData(data, documentType) {
			return data.document && data.document.length > 0 ? data.document[0] : null;
		},

		// Merge document with its additional data
		mergeDocumentData(doc, additionalData) {
			const merged = {
				...doc,
				reference: additionalData.reference,
				dunning_status: additionalData.dunning_status,
				file_references: additionalData.file_references
			};

			// Add invoice-specific data
			if (doc.document_type === 'INVOICE') {
				merged.derived_paid_at = additionalData.derived_paid_at;
				merged.invoice_dispute = additionalData.invoice_dispute;
				merged.interchange_links = additionalData.actions?.flatMap(action =>
					action.action_interchanges || []
				) || [];
			} else {
				merged.interchange_links = [];
			}

			return merged;
		},

		onSearch() {
			Logging.log('InvoiceTable.onSearch');
			this.total = 0;
			this.invoiceFiles = {};
			// Reset pagination when searching
			this.page = 1;
			this.offset = 0;
			// Always fetch invoices regardless of search term
			this.fetchInvoices();
		},
		toggleSelection(rows) {
			if (rows) {
				rows.forEach((row) => {
					this.$refs.multipleTable.toggleRowSelection(row);
				});
			} else {
				this.$refs.multipleTable.clearSelection();
			}
		},
		handleSelectionChange(val) {
			Logging.log('InvoiceTable.handleSelectionChange');
			this.multipleSelection = val;
			this.$store.dispatch('risolto/setSelectedInvoices', val);

			if (this.multipleSelection.length === 1) {
				this.$store.commit('risolto/SET_NEW_NOTE_TYPE', 'single-invoice');
				this.singleSelectedInvoiceHasReference = (val[0].file_reference || val[0].file_references.length !== 0) ? true : false;
			} else if (this.multipleSelection.length >= 1) {
				this.$store.commit('risolto/SET_NEW_NOTE_TYPE', 'multi-invoice');
			} else if (this.multipleSelection.length === 0) {
				this.$store.commit('risolto/SET_NEW_NOTE_TYPE', 'recipient');
				this.singleSelectedInvoiceHasReference = true;
			} else {
				Logging.log("Can't determine new note type");
			}
		},
		onReset() {
			this.page = 1;
			this.limit = 25;
			this.offset = 0;
			this.total = 0;
			this.sort = this.recipient ? { is_open: 'desc', issue_date: 'desc_nulls_last' } : { issue_date: 'desc_nulls_last' };

			this.deliveryEvents = {};
			this.invoiceFiles = {};

			this.status = CONSTANTS.INVOICETABLE_DEFAULT_STATUS_LIST;
			this.dateRange = null;

			// Reset document type to the default from props
			if (this.propsDocumentType && this.propsDocumentType.length > 0) {
				this.documentType = this.propsDocumentType;
			} else {
				this.documentType = CONSTANTS.INVOICETABLE_DEFAULT_TYPE_LIST;
			}

			this.searchTerm = '';

			// Reset custom fields filters
			this.customFieldsFiltersObject = {};
			this.customFieldsFiltersVisible = [];
			this.customFieldsFiltersKey += 1; // Force component re-render

			// Reset the child component if it has a reset method
			if (this.$refs.customFieldsFilters && this.$refs.customFieldsFilters.reset) {
				this.$refs.customFieldsFilters.reset();
			}

			this.fetchInvoices();
		},
		getList(data) {
			Logging.log('invoiceTable: ', data);
			this.deliveryEvents = {};
			this.invoiceFiles = {};
			this.page = data.page;
			this.limit = data.limit;
			this.offset = (data.page - 1) * data.limit;

			// The pagination components syncs and needs time to complete before fetching
			this.$nextTick(() => {
				this.fetchInvoices();
			});
		},
		loadInvoiceFiles() {
			for (const inv of this.loadController.data) {
				this.invoiceFiles[inv.reference] = [];
				for (var file of inv.file_references) {
					if (this.invoiceFiles[inv.reference]) {
						this.invoiceFiles[inv.reference].push(file);
					} else {
						this.invoiceFiles[inv.reference] = [];
						this.invoiceFiles[inv.reference].push(file);
					}
				}
			}
		},
		handleInvoiceDisputeClick(invoice) {
			this.selectedInvoice = invoice;
			this.invoiceDisputesDialogVisible = true;
		},
		async handleActionItemClick(item) {
			Logging.log('handleActionItemClick', item);
			if (!item || !item.reference) {
				return;
			}
			const result = await this.$apollo.query({
				query: GET_ACTION,
				variables: {
					participant: this.$store.state.risolto.selectedParticipantStore,
					action: item.reference
				}
			});
			const action = result.data.action[0];
			if (action.channel === 'PHONE') {
				this.$store.commit('risolto/SET_ACTION_DETAIL_DIALOG_VISIBLE', false);
				this.$store.dispatch('risolto/setActionPhoneDetailOverlayAction', action.reference);
				this.$store.dispatch('risolto/setActionPhoneDetailOverlayVisible', true);
			} else {
				this.$store.dispatch('risolto/setActionPhoneDetailOverlayVisible', false);
				this.$store.commit('actions/UPSERT_ACTION', action);
				this.$store.commit('actions/SET_SELECTED_ACTION_REF', action.reference);
				this.$store.commit('risolto/SET_SELECTED_RECIPIENT', action.recipient_by_reference);
				this.$store.commit('risolto/SET_COMPONENT_TYPE_STORE', action.channel);
				this.$store.commit('risolto/SET_ACTION_DETAIL_DIALOG_VISIBLE', true);
			}
		},
		handleCustomFiltersChange(filters) {
			this.customFieldsFiltersObject = {
				custom_fields: filters
			};
		},
		makeQuery(includeInvoices) {
			let hasRecipient = this.recipient;
			let hasDateRange = this.showFilters && this.dateRange;
			let hasSearchTerm = this.showFilters && this.searchTerm && this.searchTerm.trim() !== '';
			let hasCustomFieldsFilter = this.showFilters && this.customFieldsFiltersObject && this.customFieldsFiltersObject.custom_fields;
			let legalEntityFilter = `{ _or: [ { legal_entity: { _eq: "${this.selectedLegalEntityStore}" } }, { legal_entity: { _is_null: true } } ] }`;

			let statusFilter = '';
			if (this.status && this.status.length > 0) {
				const conditions: string[] = [];
				const today = format(startOfToday(), 'yyyy-MM-dd');

				this.status.forEach(status => {
					switch (status) {
						case 'CLOSED':
							conditions.push('{ open_amount: { _eq: 0 } }');
							break;
						case 'OPEN':
							conditions.push(`{ _and: [
								{ open_amount: { _neq: 0 } },
{ _or: [
									{ due_date: { _is_null: true } },
									{ due_date: { _gte: "${today}" } }
								] }
							] }`);
							break;
						case 'OVERDUE':
							conditions.push(`{ _and: [
								{ open_amount: { _neq: 0 } },
								{ due_date: { _is_null: false } },
								{ due_date: { _lt: "${today}" } }
							] }`);
							break;
					}
				});

				statusFilter = conditions.length > 0 ? `{ _or: [${conditions.join(', ')}] }` : '';
			}

			let where = `
where: {
	_and: [
		{ participant: { _eq: $participantRef } }
		${hasRecipient ? `{ recipient: { _eq: $recipient } }` : ``}
		${hasDateRange ? `{ issue_date: { _gte: $startTimeFrom } }` : ''}
		${hasDateRange ? `{ issue_date: { _lte: $startTimeUntil } }` : ''}
		${this.selectedLegalEntityStore != null ? legalEntityFilter : ''}
		${statusFilter ? `, ${statusFilter}` : ''}
		${this.documentType && this.documentType.length > 0 ? `{ document_type: { _in: $documentType } }` : ''}
		${hasCustomFieldsFilter ? '$customFieldsFilter' : ''}
		${hasSearchTerm ? `
		{ _or: [
			{ recipient_by_reference: { _or: [
				{ name: { _ilike: $searchTerm } },
				{ legal_id: { _ilike: $searchTerm } },
				{ account_id: { _ilike: $searchTerm } }
			] } },
			{ document_number: { _ilike: $searchTerm } },
			{ payment_reference: { _ilike: $searchTerm } }
		] }
		` : ''}
	]
}
			`;

			if (includeInvoices) {
				return gql`
query getInvoicesForInvoiceTable(
	$limit: Int = 10
	$offset: Int = 0
	$participantRef: uuid!
	${hasRecipient ? '$recipient: uuid!' : ''}
	$orderBy: [risolto_accounting_document_order_by!]
	${hasSearchTerm ? '$searchTerm: String' : ''}
	${hasDateRange ? '$startTimeFrom: date!' : ''}
	${hasDateRange ? '$startTimeUntil: date!' : ''}
	${hasCustomFieldsFilter ? '$customFieldsFilter: risolto_accounting_document_bool_exp!' : ''}
	${this.documentType && this.documentType.length > 0 ? '$documentType: [String!]' : ''}
) {
	invoices: risolto_accounting_document(
		limit: $limit
		offset: $offset
		order_by: $orderBy
		${where}
	) {
		document_number
		document_type
		open_amount
		total_amount
		payment_reference
		due_date
		issue_date
		recipient
		recipient_by_reference {
			reference
			name
		}
		legal_entity
		legal_entity_by_reference {
			reference
			registration_name
		}
		participant
		amount_currency
		custom_fields
	}
}
				`;
			} else {
				return gql`
query getInvoicesForInvoiceTable(
	$participantRef: uuid!
	${hasRecipient ? '$recipient: uuid!' : ''}
	${hasSearchTerm ? '$searchTerm: String' : ''}
	${hasDateRange ? '$startTimeFrom: date!' : ''}
	${hasDateRange ? '$startTimeUntil: date!' : ''}
	${hasCustomFieldsFilter ? '$customFieldsFilter: risolto_accounting_document_bool_exp!' : ''}
	${this.documentType && this.documentType.length > 0 ? '$documentType: [String!]' : ''}
) {
	total: risolto_accounting_document_aggregate(
		${where}
	) {
		aggregate {
			count
		}
	}
}
				`;
			}
		}
	}
});

